/**
 * @fileoverview A product screenshot on the public marketing site.
 *
 * These are photographs of the REAL pages, taken by
 * `scripts/capture-marketing.mjs` against the dev-only capture harness: the
 * shipped components in the shipped shell, with a fixture-backed axios adapter
 * standing in for the backend. So the interface is genuine and every name,
 * lesson and number in it is invented — no student, parent or instructor data
 * has ever been near one.
 *
 * They replace the hand-drawn HTML imitations the site used to carry, which
 * drifted: the landing page's "product screenshot" was still advertising a
 * subscription tier we had stopped selling. Regenerate them whenever the UI
 * moves — the harness makes that one command, which is the whole point.
 *
 * Its own module rather than a helper inside FeaturePage: the landing page
 * needs it too, and importing it from there would drag all twelve of that
 * file's mockup components into the landing bundle.
 */

import SHOT_SIZES from "./marketingShots.json";

/**
 * Fallback for an image the manifest doesn't know: the standard 1440x860
 * capture, scaled to 2000 wide. Better than omitting the attributes entirely,
 * which would reserve nothing at all.
 */
const FALLBACK = { width: 2000, height: 1194 };

export default function Screenshot({ src, alt, eager = false, className = "" }) {
  // Real pixel dimensions, written by scripts/capture-marketing.mjs. Not a
  // constant: a shot may take its own viewport — the email preview is
  // 2000x1500 where the rest are 2000x1194 — and one hardcoded aspect would
  // reserve 300px too little there and jump the page when the image landed.
  const { width, height } = SHOT_SIZES[src] || FALLBACK;

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      // Stated so the frame reserves its space before the image arrives.
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className={`block w-full h-auto ${className}`}
    />
  );
}
